import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import {
  DifficultyLevel,
  MultipleChoiceQuestionAnswer,
  MultipleChoiceQuestionAnswerType,
} from '@opositandopn/ui';
import {
  FcAcceptDatabase,
  FcAddDatabase,
  FcDataBackup,
  FcDeleteDatabase,
} from 'react-icons/fc';
import { useWindowSize } from 'usehooks-ts';

import { Loading } from '../../../../components/core/Loading';
import { AdminWidthResolution } from '../../../../constants/app';
import { useIsPremium, useProfile } from '../../../../context/ProfileContext';
import { useAppActions } from '../../../../state/appStore';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { MultipleChoiceAssessmentPreviewList } from '../../../assessments/components/multiple-choice-assessment-preview-list/MultipleChoiceAssessmentPreviewList';
import { ErrorReportForm } from '../../../reports/components/error-report-form/ErrorReportForm';
import {
  useBookmarkMultipleChoiceQuestion,
  useCreateMultipleChoiceQuestionErrorReport,
  useExplainMultipleChoiceQuestion,
  useGetMultipleChoiceQuestion,
  useUnbookmarkMultipleChoiceQuestion,
} from '../../api/useMultipleChoiceQuestions';
import { MultipleChoiceQuestionEditor } from '../multiple-choice-question-editor/MultipleChoiceQuestionEditor';

import styles from './MultipleChoiceQuestionView.module.scss';

export type ContainerProps = BaseComponentProps & {
  subjectId: string;
  questionId: string;
  selectedChoiceId?: string;
  onClose: () => void;
};

function MultipleChoiceQuestionView({
  children,
  subjectId,
  questionId,
  selectedChoiceId,
  onClose,
}: PropsWithChildren<ContainerProps>) {
  const { profile } = useProfile();
  const { setShowGetPremiumPopup } = useAppActions();
  const isPremium = useIsPremium();
  const { width } = useWindowSize();

  const [explanations, setExplanations] = useState<Record<string, string>>({});
  const [showEdit, setShowEdit] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAssessments, setShowAssessments] = useState(false);

  const {
    data: response,
    isLoading,
    isError,
  } = useGetMultipleChoiceQuestion({
    subjectId,
    questionId,
  });

  useEffect(() => {
    if (isError) {
      onClose();
    }
  }, [isError]);

  const { mutateAsync: bookmarkMultipleChoiceQuestion } =
    useBookmarkMultipleChoiceQuestion();

  const { mutateAsync: unbookmarkMultipleChoiceQuestion } =
    useUnbookmarkMultipleChoiceQuestion();

  const { mutate: createMultipleChoiceQuestionErrorReport } =
    useCreateMultipleChoiceQuestionErrorReport();

  const { explain: explainMultipleChoiceQuestion } =
    useExplainMultipleChoiceQuestion();

  const question = useMemo<MultipleChoiceQuestionAnswerType | undefined>(
    () =>
      response && {
        id: response.id,
        text: response.text,
        subject:
          response.lesson?.name ?? response.unit?.name ?? response.subject.name,
        caption:
          response.status === 'removed' ? '(Pregunta eliminada)' : undefined,
        imageUrl: response.imageUrl,
        bookmarked: response.bookmarked,
        tags: response.tags.map((tag) => tag.name),
        difficulty:
          response.difficultyLevel === 'easy'
            ? DifficultyLevel.EASY
            : response.difficultyLevel === 'medium'
              ? DifficultyLevel.MEDIUM
              : DifficultyLevel.HARD,
        options: response.choices.map((choice) => choice.text),
        selectedOptionIndex: selectedChoiceId
          ? response.choices.findIndex(
              (choice) => choice.id === selectedChoiceId,
            ) + 1
          : 0,
        reason: response.explanation,
        explanation: explanations[response.id],
        correctOptionIndex:
          response.choices.findIndex((choice) => choice.isCorrect) + 1,
        answerRate: response.metrics && {
          correct: response.metrics.correctPercentage,
          skipped: response.metrics.skippedPercentage,
          incorrect: response.metrics.incorrectPercentage,
        },
        auditLog:
          profile.role === 'admin'
            ? response.auditLog.map((log) => ({
                icon:
                  log.action === 'created' ? (
                    <FcAddDatabase />
                  ) : log.action === 'updated' ? (
                    <FcDataBackup />
                  ) : log.action === 'deleted' ? (
                    <FcDeleteDatabase />
                  ) : (
                    <FcAcceptDatabase />
                  ),
                title:
                  log.action === 'created'
                    ? 'Pregunta creada'
                    : log.action === 'updated'
                      ? 'Pregunta actualizada'
                      : log.action === 'deleted'
                        ? 'Pregunta eliminada'
                        : 'Pregunta verificada',

                description: `Por ${log.username}`,
                date: new Date(log.date),
              }))
            : undefined,
        tip:
          response.missesCount > 1 ? (
            <div className={styles['tooltip']}>
              Has fallado esta pregunta {response.missesCount} veces
            </div>
          ) : undefined,
      },
    [response, selectedChoiceId, explanations, profile.role],
  );

  if (isLoading || !question) {
    return <Loading />;
  }

  return (
    <>
      <MultipleChoiceQuestionAnswer
        question={question}
        onClose={onClose}
        onBookmark={
          profile.subscription?.isActive && response?.status !== 'removed'
            ? async (id: string) => {
                await bookmarkMultipleChoiceQuestion({
                  subjectId,
                  questionId: id,
                });
              }
            : undefined
        }
        onUnbookmark={
          profile.subscription?.isActive
            ? async (id: string) => {
                await unbookmarkMultipleChoiceQuestion({
                  subjectId,
                  questionId: id,
                });
              }
            : undefined
        }
        onFeedback={
          isPremium && response?.status !== 'removed'
            ? () => {
                setShowFeedback(true);
              }
            : undefined
        }
        onEdit={
          profile.role === 'admin' &&
          response?.status !== 'removed' &&
          width > AdminWidthResolution
            ? () => {
                setShowEdit(true);
              }
            : undefined
        }
        onPush={
          profile.role === 'admin' &&
          response?.status !== 'removed' &&
          response?.status !== 'draft' &&
          width > AdminWidthResolution
            ? () => {
                setShowAssessments(true);
              }
            : undefined
        }
        onExplain={
          response?.status !== 'removed' && !response?.imageUrl
            ? async () => {
                if (!isPremium) {
                  setShowGetPremiumPopup(true);
                  throw new Error('Premium subscription required.');
                }
                const result = await explainMultipleChoiceQuestion({
                  subjectId,
                  questionId,
                  answerId: selectedChoiceId,
                });
                setExplanations((prevExplanations) => ({
                  ...prevExplanations,
                  [questionId]: result.explanation,
                }));
              }
            : undefined
        }
      >
        {children}
      </MultipleChoiceQuestionAnswer>
      {response && (
        <MultipleChoiceQuestionEditor
          open={showEdit}
          question={response}
          onClose={() => setShowEdit(false)}
          onDeleted={onClose}
        />
      )}
      {response && (
        <MultipleChoiceAssessmentPreviewList
          open={showAssessments}
          question={response}
          onClose={() => setShowAssessments(false)}
        />
      )}
      <ErrorReportForm
        open={showFeedback}
        title="¿Has visto algún error en esta pregunta?"
        onFeedback={async (feedback) => {
          createMultipleChoiceQuestionErrorReport(
            {
              subjectId,
              questionId,
              message: feedback,
            },
            {
              onSuccess: () => {
                setShowFeedback(false);
              },
            },
          );
        }}
        onClose={() => setShowFeedback(false)}
      />
    </>
  );
}

export { MultipleChoiceQuestionView };
